/**
 * GroupInstancesPanel.tsx
 *
 * Column 3 content for the group roster: every sample or analysis in the group,
 * its status, and the two per-instance actions (fail, remove from group).
 */

import React from "react";
import { Button, Space, Tooltip, Typography } from "antd";
import { CloseCircleOutlined, DeleteOutlined, DownloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

import { DataTable, StatusTag } from "../../../../../../components/shared";
import type {
	AnalysisProtocolInstance,
	ProcessingGroup,
	SampleProtocolInstance,
} from "../../../../../../types/lab/ProtocolTypes";
import PanelHeader from "./PanelHeader";

const { Text } = Typography;

type GroupInstance = SampleProtocolInstance | AnalysisProtocolInstance;

interface GroupInstancesPanelProps {
	group: ProcessingGroup;
	groupType: "sample" | "analysis";
	/** Group must be active before an instance can be failed. */
	groupIsActive: boolean;
	onExport: () => void;
	onFail: (instance: GroupInstance) => void;
	onRemove: (instance: GroupInstance) => void;
}

const GroupInstancesPanel: React.FC<GroupInstancesPanelProps> = ({
	group,
	groupType,
	groupIsActive,
	onExport,
	onFail,
	onRemove,
}) => {
	const isSample = groupType === "sample";
	const dataSource: GroupInstance[] = isSample
		? group.sample_instances ?? []
		: group.analysis_instances ?? [];

	const noun = isSample ? "sample" : "analysis";
	const plural = isSample ? "samples" : "analyses";

	const columns: ColumnsType<GroupInstance> = [
		{
			title: isSample ? "Sample ID" : "Analysis ID",
			key: "identifier",
			render: (_: unknown, record: GroupInstance) => (
				<span className="mg-mono-num" style={{ fontWeight: 500 }}>
					{isSample
						? (record as SampleProtocolInstance).sample_id
						: `#${(record as AnalysisProtocolInstance).analysis_id}`}
				</span>
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 110,
			render: (status: string) => <StatusTag status={status || "pending"} size="small" />,
		},
		{
			title: "Note",
			dataIndex: "failure_reason",
			key: "failure_reason",
			render: (reason: string | undefined) =>
				reason ? (
					<Text type="secondary" style={{ fontSize: 11 }}>
						{reason}
					</Text>
				) : (
					<Text type="secondary" style={{ fontSize: 11 }}>
						—
					</Text>
				),
		},
		{
			title: "",
			key: "actions",
			width: 72,
			render: (_: unknown, record: GroupInstance) => {
				const status = record.status;
				const canFail = groupIsActive && status !== "failed" && status !== "completed";
				const canRemove = status === "pending";
				return (
					<Space size={2}>
						<Tooltip title={canFail ? `Fail this ${noun}` : `Cannot fail a ${status} ${noun}`}>
							<Button
								type="text"
								size="small"
								danger
								icon={<CloseCircleOutlined />}
								disabled={!canFail}
								onClick={(e) => {
									e.stopPropagation();
									onFail(record);
								}}
							/>
						</Tooltip>
						<Tooltip
							title={
								canRemove
									? "Remove from group"
									: `Only a pending ${noun} can be removed from the group`
							}
						>
							<Button
								type="text"
								size="small"
								icon={<DeleteOutlined />}
								disabled={!canRemove}
								onClick={(e) => {
									e.stopPropagation();
									onRemove(record);
								}}
							/>
						</Tooltip>
					</Space>
				);
			},
		},
	];

	return (
		<>
			<PanelHeader
				title={isSample ? "Samples" : "Analyses"}
				meta={`${dataSource.length} in this group`}
				actions={
					<Button
						size="small"
						icon={<DownloadOutlined />}
						onClick={onExport}
						disabled={dataSource.length === 0}
					>
						Export CSV
					</Button>
				}
			/>

			<div style={{ flex: 1, overflowY: "auto" }}>
				<DataTable
					bare
					columns={columns}
					dataSource={dataSource}
					rowKey="id"
					pagination={false}
					scrollX="max-content"
					emptyTitle={`No ${plural} in this group`}
					emptyDescription={`Add ${plural} to the group from the processing list to start work.`}
				/>
			</div>
		</>
	);
};

export default GroupInstancesPanel;
